/**
 * @module @verba/chat-sdk
 *
 * Verba Chat Widget SDK
 * ---------------------
 * A lightweight, zero-dependency TypeScript SDK that embeds the Verba AI chat
 * widget into any web page via a secure `<iframe>`.
 *
 * @example
 * ```ts
 * import { VerbaChat } from '@verba/chat-sdk';
 *
 * const sdk = new VerbaChat({ tagId: 'your-tag-id', theme: 'dark' });
 * sdk.init();
 * ```
 */

import { Messenger } from './messenger.ts'
import type { ChatBubbleConfig, VerbaChatConfig, WidgetState } from './types.ts'
import {
    createBubbleButton,
    createFloatingContainer,
    createGreetingPopup,
    createIframeElement,
    createInlineContainer,
    EXPANDED_CLASS,
    hideBadge,
    hideIframe,
    hidePopup,
    injectStyles,
    isMobileViewport,
    lockBodyScroll,
    MOBILE_BREAKPOINT,
    MOBILE_FULLSCREEN_CLASS,
    removeInjectedStyles,
    setBubbleOpen,
    showBadge,
    showIframe,
    showPopup,
    truncateMessage,
    unlockBodyScroll,
} from './ui.ts'

const DEFAULT_GREETING_MESSAGE = 'Hey, if you need some help, ask me anything!'
const DEFAULT_GREETING_DELAY = 2000

/** Max time to wait for the iframe to send `BUBBLE_CONFIG` before building the bubble with local-only config. */
const BUBBLE_CONFIG_FALLBACK_MS = 8000

// ─── Constants ───────────────────────────────────────────────────────────────

/**
 * The origin of the hosted widget page.
 */
const WIDGET_ORIGIN = 'https://app.verba.chat'

/** Full URL of the embeddable HTML page inside the widget origin. */
const WIDGET_URL = `${WIDGET_ORIGIN}/embed`

// ─── SDK Error ───────────────────────────────────────────────────────────────

/** Thrown when an SDK method is called in an invalid lifecycle state. */
export class VerbaChatError extends Error {
    constructor(message: string) {
        super(`[VerbaChat] ${message}`)
        this.name = 'VerbaChatError'
    }
}

// ─── VerbaChat ─────────────────────────────────────────────────────────────────

/**
 * Main class for the Verba Chat Widget.
 *
 * ### Lifecycle
 * ```
 * new VerbaChat(config)  →  .init()  →  .show() / .hide()  →  .destroy()
 * ```
 *
 * ### Floating mode (default)
 * When no `container` is provided a floating bubble button is created in the
 * bottom-right corner of the viewport. Clicking the bubble opens/closes the
 * iframe with a smooth CSS animation.
 *
 * ### Inline mode
 * When `container` is a CSS selector or `HTMLElement`, the iframe is mounted
 * directly inside that element and the bubble button is omitted.
 */
export class VerbaChat {
    // ── Config ─────────────────────────────────────────────────────────────────
    private readonly config: Required<
        Omit<
            VerbaChatConfig,
            | 'targetElement'
            | 'tagId'
            | 'bubble'
            | 'token'
            | 'tokenEnvironment'
            | 'assistantAvatar'
            | 'withThreadList'
        >
    > &
        Pick<
            VerbaChatConfig,
            | 'targetElement'
            | 'bubble'
            | 'token'
            | 'tokenEnvironment'
            | 'assistantAvatar'
            | 'withThreadList'
        > & {
            tagId: string
        }

    // ── State ──────────────────────────────────────────────────────────────────
    private state: WidgetState = 'uninitialized'
    private isVisible = false
    private isInline = false

    // ── DOM refs ───────────────────────────────────────────────────────────────
    public container: HTMLElement | null = null
    public iframe: HTMLIFrameElement | null = null
    public bubble: HTMLButtonElement | null = null
    private popup: HTMLDivElement | null = null
    private popupTextEl: HTMLParagraphElement | null = null
    private badge: HTMLDivElement | null = null

    // ── Greeting/popup state ──────────────────────────────────────────────────
    private isPopupGreeting = false
    private greetingTimeoutId: ReturnType<typeof setTimeout> | null = null
    private bubbleConfigFallbackTimeoutId: ReturnType<typeof setTimeout> | null = null

    // ── Resolved bubble config (local + BUBBLE_CONFIG/fallback merge) ────────────
    private resolvedBubbleConfig: ChatBubbleConfig | undefined

    // ── Messenger ──────────────────────────────────────────────────────────────
    private messenger: Messenger | null = null

    // ── Cleanup ────────────────────────────────────────────────────────────────
    private readonly cleanupCallbacks: Array<() => void> = []

    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Create a new `VerbaChat` instance.
     *
     * @param config - SDK configuration. Only `tagId` is required.
     *
     * @throws {VerbaChatError} if `tagId` is empty.
     */
    constructor(config: VerbaChatConfig) {
        const tagId = config.tagId
        if (!tagId?.trim()) {
            throw new VerbaChatError('`tagId` is required and must not be empty.')
        }

        this.config = {
            tagId: tagId.trim(),
            theme: config.theme ?? 'light',
            bubble: config.bubble,
            targetElement: config.targetElement,
            src: config.src ?? WIDGET_URL,
            token: config.token,
            tokenEnvironment: config.tokenEnvironment,
            // Left `undefined` when unset (not defaulted to `''`/`false`) so the
            // embed iframe can tell "not passed" apart from "explicitly set" and
            // fall back to the tenant's saved widget config correctly.
            assistantAvatar: config.assistantAvatar,
            withThreadList: config.withThreadList,
        }
    }

    // ─── Public API ───────────────────────────────────────────────────────────

    /**
     * Inject the widget into the DOM
     *
     * In **floating mode** a chat bubble is rendered in the viewport corner.
     * In **inline mode** the iframe is mounted directly into the configured container.
     *
     * @throws {VerbaChatError} if called more than once or after `destroy()`.
     */
    init(): this {
        this.assertState('uninitialized', 'init')

        injectStyles()
        this.state = 'ready'

        this.isInline = !!this.config.targetElement

        if (this.isInline) {
            this.mountInline()
        } else {
            this.mountFloating()

            this.bubbleConfigFallbackTimeoutId = setTimeout(() => {
                this.bubbleConfigFallbackTimeoutId = null
                if (!this.isInline && !this.bubble) this.buildBubble(this.config.bubble)
            }, BUBBLE_CONFIG_FALLBACK_MS)
            this.cleanupCallbacks.push(() => {
                if (this.bubbleConfigFallbackTimeoutId !== null) {
                    clearTimeout(this.bubbleConfigFallbackTimeoutId)
                    this.bubbleConfigFallbackTimeoutId = null
                }
            })
        }

        const widgetOrigin = new URL(this.config.src).origin

        this.messenger = new Messenger({
            targetWindow: this.iframe!.contentWindow!,
            targetOrigin: widgetOrigin,
            allowedOrigin: widgetOrigin,
        })

        // .on<null> because we are not waiting for any data from the widget, we just need the signal that it is ready.
        const unsubReady = this.messenger.on<null>('READY', () => {
            this.messenger?.send<VerbaChatConfig>('INIT_WIDGET', this.config)
            unsubReady()
        })
        this.cleanupCallbacks.push(unsubReady)

        const unsubExpand = this.messenger.on<null>('EXPAND_WIDGET', () => {
            this.setExpandedState(true)
        })
        this.cleanupCallbacks.push(unsubExpand)

        const unsubShrink = this.messenger.on<null>('SHRINK_WIDGET', () => {
            this.setExpandedState(false)
        })
        this.cleanupCallbacks.push(unsubShrink)

        const unsubClose = this.messenger.on<null>('CLOSE_WIDGET', () => {
            this.hide()
        })
        this.cleanupCallbacks.push(unsubClose)

        const unsubMessage = this.messenger.on<{ message: string }>(
            'SHOW_WIDGET_MESSAGE',
            (data) => {
                this.showMessagePopup(data?.message)
            }
        )
        this.cleanupCallbacks.push(unsubMessage)

        const unsubBubbleConfig = this.messenger.on<ChatBubbleConfig>('BUBBLE_CONFIG', (config) => {
            if (this.isInline) return
            this.buildBubble(config)
        })
        this.cleanupCallbacks.push(unsubBubbleConfig)

        return this
    }

    /**
     * Make the widget visible.
     * In floating mode this opens the iframe above the bubble.
     *
     * @throws {VerbaChatError} if `init()` has not been called.
     */
    show(): this {
        this.assertNotState('uninitialized', 'show')
        this.assertNotState('destroyed', 'show')

        if (this.isVisible || !this.iframe) return this

        this.isVisible = true

        if (!this.isInline) {
            showIframe(this.iframe)
            if (this.bubble) setBubbleOpen(this.bubble, true)
            this.applyMobileFullscreenState()
            this.hidePopupAndPersistGreeting()
            if (this.badge) hideBadge(this.badge)
        }

        return this
    }

    /**
     * Hide the widget (does not destroy it — all state is preserved).
     *
     * @throws {VerbaChatError} if `init()` has not been called.
     */
    hide(): this {
        this.assertNotState('uninitialized', 'hide')
        this.assertNotState('destroyed', 'hide')

        if (!this.isVisible || !this.iframe) return this

        this.isVisible = false

        if (!this.isInline) {
            hideIframe(this.iframe)
            if (this.bubble) setBubbleOpen(this.bubble, false)
            this.container?.classList.remove(MOBILE_FULLSCREEN_CLASS)
            unlockBodyScroll()
        }

        return this
    }

    /**
     * Completely tear down the SDK:
     *  - Removes all DOM elements it created.
     *  - Removes all event listeners.
     *  - Resets state to `'destroyed'`.
     *
     * After calling `destroy()` this instance cannot be reused — create a new one.
     */
    destroy(): void {
        if (this.state === 'destroyed') return

        this.container?.remove()
        this.container = null
        this.iframe = null
        this.bubble = null
        this.popup = null
        this.popupTextEl = null
        this.badge = null

        for (const cb of this.cleanupCallbacks) cb()
        this.cleanupCallbacks.length = 0

        removeInjectedStyles()
        unlockBodyScroll()

        this.state = 'destroyed'
        this.isVisible = false
        this.messenger?.destroy()
    }

    /** Current lifecycle state of this SDK instance. */
    get currentState(): WidgetState {
        return this.state
    }

    // ─── Private: Mount Strategies ───────────────────────────────────────────

    private mountInline(): void {
        const host = this.resolveContainer()
        if (!host) {
            console.error(
                '[VerbaVerbaChat] Container element not found. Falling back to floating mode.'
            )
            return
        }

        const wrapper = createInlineContainer()
        const iframe = createIframeElement(this.buildWidgetUrl())

        wrapper.appendChild(iframe)
        host.appendChild(wrapper)

        this.container = wrapper
        this.iframe = iframe
        this.isVisible = true
    }

    private mountFloating(): void {
        const position = this.config.bubble?.position ?? 'bottom-right'
        const wrapper = createFloatingContainer(position)
        const iframe = createIframeElement(this.buildWidgetUrl())

        // Keep fullscreen state in sync across resizes/orientation changes while open
        const mobileMql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`)
        const onMobileBreakpointChange = (): void => {
            if (this.isVisible) this.applyMobileFullscreenState()
        }
        mobileMql.addEventListener('change', onMobileBreakpointChange)
        this.cleanupCallbacks.push(() =>
            mobileMql.removeEventListener('change', onMobileBreakpointChange)
        )

        // Custom popover, hide when clicking outside of the iframe
        const cPopover = (event: MouseEvent): void => {
            if (!this.isVisible) return

            if (
                this.container &&
                event.target instanceof Node &&
                !this.container.contains(event.target)
            ) {
                this.hide()
            }
        }

        const escapeKey = (event: KeyboardEvent): void => {
            if (!this.isVisible) return

            if (event.key === 'Escape') {
                this.hide()
            }
        }

        document.addEventListener('click', cPopover, true)
        this.cleanupCallbacks.push(() => document.removeEventListener('click', cPopover, true))

        document.addEventListener('keydown', escapeKey, true)
        this.cleanupCallbacks.push(() => document.removeEventListener('keydown', escapeKey, true))

        wrapper.appendChild(iframe)
        document.body.appendChild(wrapper)

        this.container = wrapper
        this.iframe = iframe
        this.isVisible = false
    }

    /**
     * Builds the bubble button, badge, and greeting popup, and wires up their
     * event listeners. Deferred until the widget config is known — called either
     * from the `BUBBLE_CONFIG` handler (server-merged config) or the fallback
     * timeout (local-only config), whichever fires first. Idempotent: a no-op if
     * the bubble has already been built.
     */
    private buildBubble(config: ChatBubbleConfig | undefined): void {
        if (this.bubble || !this.container) return

        if (this.bubbleConfigFallbackTimeoutId !== null) {
            clearTimeout(this.bubbleConfigFallbackTimeoutId)
            this.bubbleConfigFallbackTimeoutId = null
        }

        this.resolvedBubbleConfig = config

        // `mountFloating()` set `data-position` from local-only config before this
        // resolved (possibly server-merged) config was known — sync it now.
        this.container.dataset.position = config?.position ?? 'bottom-right'

        const { wrapper: bubbleWrapper, button: bubble, badge } = createBubbleButton(config)
        const { popup, dismissButton, textEl } = createGreetingPopup()

        // Bubble click → toggle show/hide
        const onBubbleClick = (): void => {
            this.isVisible ? this.hide() : this.show()
        }
        bubble.addEventListener('click', onBubbleClick)
        this.cleanupCallbacks.push(() => bubble.removeEventListener('click', onBubbleClick))

        // Dismiss button → hide the popup (persists forever if it was the greeting)
        const onDismissClick = (event: MouseEvent): void => {
            event.stopPropagation()
            this.dismissPopup()
        }
        dismissButton.addEventListener('click', onDismissClick)
        this.cleanupCallbacks.push(() => dismissButton.removeEventListener('click', onDismissClick))

        // Clicking the popup body opens the chat
        const onPopupClick = (): void => {
            this.show()
        }
        popup.addEventListener('click', onPopupClick)
        this.cleanupCallbacks.push(() => popup.removeEventListener('click', onPopupClick))

        this.container.appendChild(popup)
        this.container.appendChild(bubbleWrapper)

        this.bubble = bubble
        this.popup = popup
        this.popupTextEl = textEl
        this.badge = badge

        const greeting = config?.greeting
        if (!greeting?.disabled) {
            const delay = greeting?.delay ?? DEFAULT_GREETING_DELAY
            this.greetingTimeoutId = setTimeout(() => {
                this.greetingTimeoutId = null
                this.maybeShowGreeting()
            }, delay)
            this.cleanupCallbacks.push(() => {
                if (this.greetingTimeoutId !== null) {
                    clearTimeout(this.greetingTimeoutId)
                    this.greetingTimeoutId = null
                }
            })
        }
    }

    // ─── Private: Mobile Fullscreen ──────────────────────────────────────────

    /**
     * Syncs the floating widget's fullscreen state with the current viewport.
     * No-op in inline mode. Safe to call whenever the widget is visible.
     */
    private applyMobileFullscreenState(): void {
        if (this.isInline || !this.container) return

        const shouldBeFullscreen = isMobileViewport()
        this.container.classList.toggle(MOBILE_FULLSCREEN_CLASS, shouldBeFullscreen)

        if (shouldBeFullscreen) {
            lockBodyScroll()
        } else {
            unlockBodyScroll()
        }
    }

    // ─── Private: Expand/Shrink ───────────────────────────────────────────────

    /**
     * Toggles the enlarged iframe panel requested by the widget via
     * `EXPAND_WIDGET`/`SHRINK_WIDGET` postMessages. No-op in inline mode,
     * since inline widgets already fill their host container.
     */
    private setExpandedState(expanded: boolean): void {
        if (this.isInline || !this.container) return

        this.container.classList.toggle(EXPANDED_CLASS, expanded)
    }

    // ─── Private: Greeting / Notification Popup ──────────────────────────────

    /**
     * Shows the first-time greeting popup, unless it's already been dismissed
     * (persisted in `localStorage`), the chat is already open, or disabled via
     * `bubble.greeting.disabled`. No-op in inline mode.
     */
    private maybeShowGreeting(): void {
        if (this.isInline || !this.popup || !this.popupTextEl) return
        if (this.isVisible) return

        const greeting = this.resolvedBubbleConfig?.greeting
        if (greeting?.disabled) return
        if (this.readGreetingDismissed()) return

        const message = greeting?.message ?? DEFAULT_GREETING_MESSAGE
        if (!message.trim()) return

        this.popupTextEl.textContent = message
        this.isPopupGreeting = true
        showPopup(this.popup)
    }

    /**
     * Shows a truncated preview of `message` in the popup, triggered by the
     * embedded widget via the `SHOW_WIDGET_MESSAGE` postMessage. Only relevant
     * while the chat is closed — no-op in inline mode or while the chat is open.
     * Always supersedes (and permanently dismisses) the first-time greeting.
     */
    private showMessagePopup(message: string | undefined): void {
        if (this.isInline || !this.popup || !this.popupTextEl) return
        if (this.isVisible || !message?.trim()) return

        if (this.greetingTimeoutId !== null) {
            clearTimeout(this.greetingTimeoutId)
            this.greetingTimeoutId = null
        }

        this.popupTextEl.textContent = truncateMessage(message)
        this.isPopupGreeting = false
        this.writeGreetingDismissed()
        showPopup(this.popup)
        if (this.badge) showBadge(this.badge)
    }

    /** Dismiss button handler — hides the popup and persists the greeting dismissal. */
    private dismissPopup(): void {
        if (!this.popup) return

        hidePopup(this.popup)
        if (this.isPopupGreeting) {
            this.writeGreetingDismissed()
        }
    }

    /** Hides the popup (if visible) and persists the greeting dismissal — used when the chat opens. */
    private hidePopupAndPersistGreeting(): void {
        if (!this.popup) return

        hidePopup(this.popup)
        this.writeGreetingDismissed()
    }

    private get greetingStorageKey(): string {
        return `verba-chat-sdk:greeting-dismissed:${this.config.tagId}`
    }

    private readGreetingDismissed(): boolean {
        try {
            return window.localStorage.getItem(this.greetingStorageKey) === '1'
        } catch {
            return false
        }
    }

    private writeGreetingDismissed(): void {
        try {
            window.localStorage.setItem(this.greetingStorageKey, '1')
        } catch {
            // Storage unavailable (e.g. private browsing) — the greeting just won't persist.
        }
    }

    // ─── Private: State Guards ───────────────────────────────────────────────

    private assertState(expected: WidgetState, method: string): void {
        if (this.state !== expected) {
            throw new VerbaChatError(
                `Cannot call \`${method}()\` in state "${this.state}". Expected "${expected}".`
            )
        }
    }

    private assertNotState(forbidden: WidgetState, method: string): void {
        if (this.state === forbidden) {
            throw new VerbaChatError(`Cannot call \`${method}()\` in state "${forbidden}".`)
        }
    }

    // ─── Private: Helpers ────────────────────────────────────────────────────

    private resolveContainer(): HTMLElement | null {
        const { targetElement } = this.config
        if (!targetElement) return null
        if (typeof targetElement === 'string') {
            return document.querySelector<HTMLElement>(targetElement)
        }
        return targetElement
    }

    private buildWidgetUrl(): string {
        const url = new URL(this.config.src)

        /*
         * Works in production, will fail in development
         * Mainly used for cache invalidation
         */
        url.searchParams.set('sdk-version', import.meta.env.PACKAGE_VERSION)

        return url.toString()
    }
}

// ─── Re-exports ───────────────────────────────────────────────────────────────

export type {
    BubblePosition,
    PostMessagePayload,
    Theme,
    ThemeConfig,
    VerbaChatConfig,
    WidgetState,
} from './types.ts'
